/**
 * Sobre.
 *
 * Duas perguntas, uma tela. "O que mudou?" — a lista de novidades, escrita para
 * quem usa. "Que versão eu estou usando?" — o carimbo do build, que é o que
 * transforma "acho que não atualizou" em um commit e uma hora que dá para
 * comparar com o que foi publicado.
 *
 * Vale para o cliente também, e não só para a equipe: quem fica com o app
 * antigo aberto no celular a semana inteira é ele.
 *
 * Sem crachá de "novo" e sem folha que sobe sozinha ao atualizar: a tela é
 * procurada, não empurrada. Interromper o barbeiro no meio de um corte para
 * contar que a agenda abre no horário de agora é pior do que ele descobrir
 * sozinho, usando.
 */

import { RELEASE_NOTES } from '@barbearia/shared';
import { useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../App.tsx';
import { BUILD, shortCommit } from '../lib/build.ts';
import { useTenant } from '../lib/contexts.tsx';
import { dayMonthAndYear, fullDateOf, timeOf } from '../lib/format.ts';

export function About() {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  return (
    <div className="flex min-h-full flex-col pb-10">
      <ScreenHeader
        title="Sobre"
        action={
          <button
            type="button"
            onClick={() => navigate('/ajustes')}
            className="touch-target shrink-0 px-2 text-[15px] font-medium text-ink-soft"
          >
            Voltar
          </button>
        }
      />

      <section className="px-4 pt-5">
        <div className="rounded-xl border border-line bg-surface-raised px-4 py-3.5">
          <p className="text-[15px] font-medium text-ink">BarbearIA</p>
          {BUILD ? (
            <>
              <p className="pt-0.5 text-[13px] text-ink-soft tabular-nums">
                {fullDateOf(BUILD.builtAt, tenant.timezone)} às{' '}
                {timeOf(BUILD.builtAt, tenant.timezone)}
              </p>
              <p className="pt-0.5 font-mono text-[12px] text-ink-faint">
                {shortCommit(BUILD.commit)}
              </p>
            </>
          ) : (
            <p className="pt-0.5 text-[13px] text-ink-soft">Desenvolvimento</p>
          )}
        </div>
        <p className="px-1 pt-2 text-[12px] text-ink-faint">
          {BUILD
            ? 'A data é de quando esta versão foi publicada. Se ela estiver velha depois de uma atualização, feche o app e abra de novo.'
            : 'Esta cópia não veio de uma publicação — foi construída na máquina de quem desenvolve.'}
        </p>
      </section>

      <section className="px-4 pt-6">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Novidades
        </h2>

        <ol className="flex flex-col gap-4">
          {RELEASE_NOTES.map((note) => (
            <li
              key={note.date}
              className="rounded-xl border border-line bg-surface-raised px-4 py-3"
            >
              <p className="text-[13px] font-medium text-ink-faint">{dayMonthAndYear(note.date)}</p>
              <ul className="flex flex-col gap-1.5 pt-2">
                {note.items.map((item) => (
                  <li key={item} className="flex gap-2 text-[15px] leading-snug text-ink">
                    <span aria-hidden className="pt-1 text-[10px] text-primary">
                      ●
                    </span>
                    <span className="min-w-0">{item}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
